import { memo, useContext } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import RelativeLink from "app/pages/.shared/RelativeLink";
import NewBadge from "app/pages/Header/conditionnalComponents/NewBadge";

import "./AccountAsideMenuAuthenticated.scss";
import AmountContainer from "app/pages/.shared/AmountContainer";
import { brandPropTypes } from "app/utils/propTypes";
import { BRANDS } from "app/constants";
import DotNotification from "app/pages/.shared/DotNotification/DotNotification";
import AppGlobalsContext from "app/AppGlobalsContext";
import AccountAsideMenuFooter from "app/pages/Account/AccountAsideMenu/AccountAsideMenuFooter";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import IconAccount from "app/pages/.shared/IconAccount/IconAccount";
import { HelpCircleFilledIcon } from "app/icons/navigation";
import { MoneyBagFilledIcon } from "app/icons/finance";
import { ICON_SIZES } from "app/constants";

const AIMessageIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M13.1797 1.75C13.8419 1.74999 14.2155 1.75036 14.5371 1.77246C19.1622 2.09051 22.8429 5.73618 23.1641 10.3174C23.1864 10.6359 23.1865 11.0845 23.1865 11.7402C23.1865 12.3961 23.1864 12.7664 23.1641 13.085C23.0497 14.7169 22.5089 16.2317 21.6504 17.5186L23.1123 21.126C23.2215 21.3959 23.1624 21.7044 22.9609 21.916C22.7594 22.1277 22.4514 22.2037 22.1729 22.1113L17.9756 20.7188C16.9256 21.2275 15.7639 21.5465 14.5371 21.6309C14.2155 21.653 13.8419 21.6523 13.1797 21.6523H11.5938C10.9318 21.6523 10.5579 21.6529 10.2363 21.6309C5.61112 21.3128 1.92949 17.6663 1.6084 13.085C1.5861 12.7664 1.58692 12.3961 1.58692 11.7402C1.58692 11.0845 1.5861 10.6359 1.6084 10.3174C1.92955 5.73612 5.61116 2.09046 10.2363 1.77246C10.5579 1.75037 10.9317 1.74999 11.5938 1.75H13.1797ZM12.3877 7.2002C12.1296 7.20028 11.9054 7.36445 11.8223 7.60059L11.7949 7.70605L11.7588 7.93848C11.4675 9.78944 9.97826 11.2202 8.11719 11.4375C7.81495 11.4729 7.58692 11.7298 7.58692 12.0342C7.58719 12.3383 7.81516 12.5935 8.11719 12.6289C9.98018 12.8464 11.4729 14.2768 11.7695 16.1289L11.7949 16.2949C11.8415 16.5859 12.0931 16.7997 12.3877 16.7998C12.6823 16.7997 12.9338 16.5859 12.9805 16.2949L13.0078 16.126C13.3043 14.2753 14.7946 12.8463 16.6563 12.6289C16.9582 12.5934 17.1863 12.3382 17.1865 12.0342C17.1865 11.7299 16.9584 11.473 16.6563 11.4375C14.7964 11.2203 13.3086 9.79019 13.0176 7.94043L12.9805 7.70605C12.9343 7.41469 12.6828 7.20028 12.3877 7.2002Z"
			fill="url(#paint0_linear_13991_44259)"
		/>
		<defs>
			<linearGradient
				id="paint0_linear_13991_44259"
				x1="0.0770127"
				y1="1.09399"
				x2="22.5311"
				y2="24.8595"
				gradientUnits="userSpaceOnUse"
			>
				<stop offset="0.0707406" stopColor="#626EB3" />
				<stop offset="0.912405" stopColor="#764CA4" />
			</linearGradient>
		</defs>
	</svg>
);

const AccountAsideMenuAuthenticated = ({
	enableSponsorship,
	enableCredits,
	creditsAmount,
	creditNotesAmount,
	partnerCode,
	brand,
	shouldDisplayCreditNotesEntry,
	showCustomerRequestNotification,
	userIsConnected = false,
	logout = () => {},
}) => {
	const isBrandXX = brand === BRANDS.XX;

	const { shop } = useContext(AppGlobalsContext);

	return (
		<nav
			className="account-aside-menu-authenticated"
			data-testid="account-aside-menu-authenticated"
		>
			<ul className="account-aside-menu-authenticated__list">
				<li className="sub-nav">
					<div className="sub-nav__main" style={{ paddingBottom: "8px" }}>
						<div className="sub-nav__label">
							<IconAccount size={ICON_SIZES.LG} />
							<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
								<FormattedMessage id="menu.mobile.my.account" />
							</Typography>
						</div>
					</div>
					<ul className="sub-nav__list">
						{!isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{ pathname: "/account/profile" }}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.profile" />
								</RelativeLink>
							</li>
						)}

						{enableCredits && (
							<li className="sub-nav__item sub-nav__item-with-chip">
								<RelativeLink
									to={{
										pathname: "/account/mycredits",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.credits" tagName="span" />
									{creditsAmount > 0 && (
										<div className="sub-nav__chip chip--credit">
											<MoneyBagFilledIcon />
											<AmountContainer amount={creditsAmount} />
										</div>
									)}
								</RelativeLink>
							</li>
						)}

						{isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/account/mybookings",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.bookings" />
								</RelativeLink>
							</li>
						)}

						{shouldDisplayCreditNotesEntry && (
							<li className="sub-nav__item sub-nav__item-with-chip">
								<RelativeLink
									to={{
										pathname: "/account/myrefunds",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.refunds" tagName="span" />
									{creditNotesAmount > 0 && (
										<div className="sub-nav__chip">
											<AmountContainer amount={creditNotesAmount} />
										</div>
									)}
								</RelativeLink>
							</li>
						)}

						{isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									rel="noopener noreferrer"
									target="_blank"
									to={{
										pathname:
											!creditNotesAmount && shop === "fr-FR"
												? `https://www.perfectstay.com/fr-FR`
												: "/listing",
									}}
								>
									<FormattedMessage id="menu.mobile.all.sales" />
								</RelativeLink>
							</li>
						)}

						{enableSponsorship && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/account/sponsorship",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.sponsorship" />
									<NewBadge partnerCode={partnerCode} />
								</RelativeLink>
							</li>
						)}

						{!isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/account/mybookings",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.bookings" />
								</RelativeLink>
							</li>
						)}

						{!isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/account/subscriptions",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.subscriptions" />
								</RelativeLink>
							</li>
						)}

						{!isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/account/security",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.security" />
								</RelativeLink>
							</li>
						)}

						{userIsConnected && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/",
									}}
									onClick={() => logout()}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.logout" />
								</RelativeLink>
							</li>
						)}
					</ul>
				</li>
				<li className="sub-nav">
					<div className="sub-nav__main" style={{ paddingBottom: "8px" }}>
						<div className="sub-nav__label">
							<HelpCircleFilledIcon height={20} />
							<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
								<FormattedMessage id="menu.item.need.help" />
							</Typography>
						</div>
					</div>
					<ul className="sub-nav__list">
						<li className="sub-nav__item">
							<RelativeLink
								to={{ pathname: "/account/faq-contact" }}
								activeClassName="sub-nav__item--active"
							>
								<FormattedMessage id="menu.item.faq.contact" />
							</RelativeLink>
						</li>
						<li className="sub-nav__item sub-nav__item__chatbot">
							<RelativeLink
								to={{ pathname: "/account/faq-chatbot" }}
								activeClassName="sub-nav__item--active"
							>
								<FormattedMessage id="menu.item.faq.chatbot" />
								<AIMessageIcon />
							</RelativeLink>
						</li>
						<li className="sub-nav__item">
							<RelativeLink
								to={{ pathname: "/account/customer-request/my-requests" }}
								activeClassName="sub-nav__item--active"
							>
								<FormattedMessage id="menu.item.my.requests" />
								{showCustomerRequestNotification && <DotNotification />}
							</RelativeLink>
						</li>
					</ul>
				</li>
				<AccountAsideMenuFooter />
			</ul>
		</nav>
	);
};

AccountAsideMenuAuthenticated.propTypes = {
	enableSponsorship: PropTypes.bool,
	showCustomerRequestNotification: PropTypes.bool,
	shouldDisplayCreditNotesEntry: PropTypes.bool,
	userIsConnected: PropTypes.bool,
	enableCredits: PropTypes.bool,
	partnerCode: PropTypes.string,
	creditsAmount: PropTypes.number,
	creditNotesAmount: PropTypes.number,
	brand: brandPropTypes,
	logout: PropTypes.func,
};

export default memo(AccountAsideMenuAuthenticated);
